import Service from "@ember/service";
import { tracked } from "@glimmer/tracking";
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";

export enum Feature {
  DmpConnectIsDevelopmentMode = "DmpConnectIsDevelopmentMode"
}

type FeatureActivations = Record<Feature, boolean>;

/**
 * A service for handling feature flipping.
 * The status is stored in the local storage.
 */
export default class FeaturesService extends Service {
  @tracked private features: FeatureActivations =
    FeaturesService.storedFeatures;

  isEnabled(feature: Feature): boolean {
    return this.features[feature];
  }

  enable(feature: Feature): void {
    const features = FeaturesService.storedFeatures;
    features[feature] = true;
    FeaturesService.storeFeatures(features);
    this.features = features;
  }

  disable(feature: Feature): void {
    const features = FeaturesService.storedFeatures;
    features[feature] = false;
    FeaturesService.storeFeatures(features);
    this.features = features;
  }

  reset(): void {
    FeaturesService.storeFeatures(FeaturesService.defaultFeatures);
    this.features = FeaturesService.defaultFeatures;
  }

  static readonly featuresKey = "features:activations";

  private static defaultFeatures = {
    DmpConnectIsDevelopmentMode: false
  };

  private static get storedFeatures(): FeatureActivations {
    const serializedFeatures = getItemFromLocalStorage(
      FeaturesService.featuresKey
    );
    if (serializedFeatures) {
      return JSON.parse(serializedFeatures);
    }

    return this.defaultFeatures;
  }

  private static storeFeatures(features: FeatureActivations): void {
    setItemInLocalStorage(
      FeaturesService.featuresKey,
      JSON.stringify(features)
    );
  }
}

declare module "@ember/service" {
  interface Registry {
    "semeia-features": FeaturesService;
  }
}
